import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import React from 'react';

import PropertyDropdown from '../common/PropertyDropdown';

import type { ElementState } from '../../renderer/ElementState';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

type Props = {
  activeElement: ElementState;
  propertyName: string;
  defaultValue: any;
  options: Array<{ caption: string; value: any }>;
  editorStore: EditorStoreLike;
};
const ANIMATION_SUBS_TO_REPLACE = [
  'panLeft',
  'panRight',
  'panLeftWithZoom',
  'panRightWithZoom',
];

const ImageFitPropertySelect: React.FC<Props> = observer(props => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = props.editorStore;
  const elementId = props.activeElement.source.id;
  function getActiveProperty() {
    if (videoCreator.isImageElementComposition(props.activeElement)) {
      return 'black-frame';
    }
    return props.activeElement.source[props.propertyName] ?? props.defaultValue;
  }
  const source = videoCreator.getImageElement(props.activeElement)?.source;

  const updateImageFit = async (value: any) => {
    // Build flat property updates — commandUpdateElement / ElementTypeTransitionCommand
    // will handle ImageElement ↔ CompositionElement class transitions automatically
    // when it detects a `fit` value change that requires a different element type.
    const updates: Record<string, any> = {
      width: '100%',
      height: '100%',
      x: '50%',
      y: '50%',
    };

    if (value === 'black-frame') {
      // Replace pan animations with zoom-in (panning doesn't work inside compositions)
      const animations =
        source?.animations?.map((a: any) => {
          if (ANIMATION_SUBS_TO_REPLACE.includes(a.arbor_subType)) {
            return {
              easing: 'linear',
              type: 'scale',
              arbor_subType: 'zoomIn',
              scope: 'element',
              start_scale: '100%',
              end_scale: '130%',
              fade: false,
            };
          }
          return a;
        }) || [];

      // Pass fit: 'black-frame' so UpdateElementCommand.detectTypeTransition
      // routes to ElementTypeTransitionCommand which creates a proper
      // CompositionElement with child elements via getImageWithBlackFrameElements.
      updates.fit = 'black-frame';
      updates.duration = props.activeElement.duration;
      updates.animations = animations;
      updates.color_overlay = source?.color_overlay || '';
    } else {
      updates[props.propertyName] = value;
      updates.source = source?.source;
      updates.locked = false;
      updates.type = 'image';
      updates.animations = props.activeElement.source?.animations || [];
      updates.color_overlay = props.activeElement.source?.color_overlay || '';

      if (!videoCreator.hasLandscapeAspectRatio() && value === 'cover') {
        // adjustModificationsToAspectRatio uses dot-path keys
        const dotPathMods: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(updates)) {
          dotPathMods[`${elementId}.${key}`] = val;
        }
        await videoCreator.adjustModificationsToAspectRatio(dotPathMods, {
          ...videoCreator.getElementSource(props.activeElement),
          [props.propertyName]: value,
        });
        // Extract flat properties back from dot-path modifications
        for (const [key, val] of Object.entries(dotPathMods)) {
          updates[key.replace(`${elementId}.`, '')] = val;
        }
      }
    }

    const updatesMap = new Map<string, Record<string, any>>();
    updatesMap.set(elementId, updates);

    const extendedMap = videoCreator.trackManager
      ? videoCreator.trackManager.prepareToUpdateFrameLockedTracks(updatesMap)
      : updatesMap;

    // update single element if track is not frame-locked
    if (extendedMap.size <= 1) {
      editorStore.editor.commandUpdateElement(elementId, updates);
    } else {
      // propagate change to all elements of frame-locked track
      editorStore.editor.commandUpdateMultipleElements(extendedMap);
    }

    await videoCreator.setActiveElements(elementId);
  };

  return (
    <PropertyDropdown
      value={getActiveProperty()}
      onChange={updateImageFit}
      defaultValue={getActiveProperty()}
      values={props.options.map(({ value, caption }) => ({ value, caption }))}
    />
  );
});

export default ImageFitPropertySelect;
